import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Pencil, RotateCw } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Bulk rename qualifiers modal. Shows the current 32 placeholder/known teams
 * with an editable name input per row. Admin can update any subset and
 * click Save — only edited rows are sent to the bulk-rename endpoint.
 *
 * Used when ESPN auto-rename hasn't filled in actual qualifiers yet and the
 * league admin wants to seed names manually (e.g., on tournament creation
 * day before R32 fixtures are published).
 */
export default function BulkRenameQualifiersModal({ open, onOpenChange, leagueId, teams, onSaved }) {
    const sortedTeams = useMemo(
        () => [...(teams || [])].sort((a, b) => (a.seed || 0) - (b.seed || 0)),
        [teams]
    );
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(false);

    const handleChange = (slot, value) => {
        setEdits(prev => ({ ...prev, [slot]: value }));
    };

    const editedCount = Object.keys(edits).filter(s => {
        const original = sortedTeams.find(t => t.bracket_slot === s);
        return original && edits[s] !== undefined && edits[s].trim() !== "" && edits[s].trim() !== original.name;
    }).length;

    const handleSave = async () => {
        // Only send rows the admin actually edited (and that differ from current name)
        const renames = {};
        for (const slot of Object.keys(edits)) {
            const original = sortedTeams.find(t => t.bracket_slot === slot);
            const newName = (edits[slot] || "").trim();
            if (original && newName && newName !== original.name) {
                renames[slot] = newName;
            }
        }
        if (Object.keys(renames).length === 0) {
            toast.info("No changes to save.");
            return;
        }
        setSaving(true);
        try {
            const r = await api.bulkRenameTeams(leagueId, renames);
            toast.success(`Renamed ${r.renamed_count} team(s).`);
            setEdits({});
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk rename failed");
        } finally {
            setSaving(false);
        }
    };

    const handleReset = () => setEdits({});

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid="bulk-rename-modal"
                className="bg-ink border-2 border-cyber text-white max-w-2xl max-h-[90dvh] overflow-y-auto"
            >
                <DialogHeader>
                    <DialogTitle className="font-display font-extrabold text-xl uppercase flex items-center gap-2">
                        <Pencil className="w-5 h-5 text-cyber" /> Bulk Rename Qualifiers
                    </DialogTitle>
                    <DialogDescription className="text-zinc-400 text-xs">
                        Edit any team's name below. Empty rows and unchanged rows are skipped.
                        Use this when ESPN data hasn't filled in real qualifier names yet.
                        Blocked once the draft has started.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-[55dvh] overflow-y-auto pr-1">
                    {sortedTeams.map(t => {
                        const slot = t.bracket_slot || "—";
                        const editedValue = edits[slot];
                        const currentValue = editedValue !== undefined ? editedValue : t.name;
                        const hasChange = editedValue !== undefined && editedValue.trim() !== "" && editedValue.trim() !== t.name;
                        return (
                            <div key={t.id} className={`brutal-card-light p-2 ${hasChange ? "border-cyber" : ""}`}>
                                <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                                    <span className="font-mono text-cyber">{slot}</span>
                                    <span>· Seed {t.seed}</span>
                                    <span>{t.flag}</span>
                                </div>
                                <input
                                    data-testid={`bulk-rename-input-${slot}`}
                                    type="text"
                                    value={currentValue}
                                    onChange={e => handleChange(slot, e.target.value)}
                                    placeholder={t.name}
                                    className="w-full bg-ink border-2 border-[#333] rounded px-2 py-1 text-sm focus:border-cyber focus:outline-none"
                                />
                            </div>
                        );
                    })}
                </div>

                <DialogFooter className="flex-row gap-2 sm:gap-3 mt-2">
                    <Button
                        data-testid="bulk-rename-reset-btn"
                        variant="ghost"
                        type="button"
                        onClick={handleReset}
                        disabled={Object.keys(edits).length === 0 || saving}
                        className="text-xs"
                    >
                        <RotateCw className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                    <div className="flex-1" />
                    <span className="text-xs text-zinc-500 self-center">
                        {editedCount} change{editedCount === 1 ? "" : "s"} pending
                    </span>
                    <Button
                        data-testid="bulk-rename-save-btn"
                        type="button"
                        onClick={handleSave}
                        disabled={editedCount === 0 || saving}
                        className="brutal-btn-primary"
                    >
                        {saving ? "Saving..." : `Save ${editedCount || ""}`.trim()}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
